<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <div v-if="requiresIpVerification" class="ip-verification">
            <h2 class="ip-verification__heading">
                {{ $t('forms.auth.login.microcopy.verify_identity') }}
            </h2>
            <p class="ip-verification__message">{{ verificationMessage }}</p>

            <div v-if="destinationDisplay" class="ip-verification__destination">
                <div class="ip-verification__destination-icon">
                    <common-icon :name="isSmsChannel ? 'phone' : 'email'" size="small"></common-icon>
                </div>
                <div class="ip-verification__destination-copy">
                    <span class="ip-verification__destination-label">
                        {{ $t('forms.auth.login.microcopy.code_sent_to') }}
                    </span>
                    <strong class="ip-verification__destination-value">{{ destinationDisplay }}</strong>
                </div>
            </div>

            <verification-code-input
                :key="codeInputKey"
                :hasError="Boolean(verifyTokenError)"
                :isDisabled="isSending || isDisabled"
                @change="onVerificationCodeChange"
            ></verification-code-input>

            <p v-if="verifyTokenError" class="ip-verification__error">{{ verifyTokenError }}</p>

            <div class="ip-verification__actions">
                <common-button
                    :label="$t('forms.auth.login.microcopy.cancel')"
                    type="secondary"
                    :isSubmittable="false"
                    :isDisabled="isSending || isDisabled"
                    :isFluid="true"
                    @click="cancelIpVerification()"
                ></common-button>
                <common-button
                    :class="{ 'ip-verification__verify--ready': isVerifyCodeComplete }"
                    :isProcessing="isSending || isDisabled"
                    :isDisabled="!isVerifyCodeComplete"
                    :label="$t('forms.auth.login.microcopy.verify')"
                    type="primary"
                    :isFluid="true"
                ></common-button>
            </div>

            <div class="ip-verification__footer">
                <p class="ip-verification__footer-line">
                    {{ $t('forms.auth.login.microcopy.didnt_receive') }}
                    <button
                        type="button"
                        class="ip-verification__text-action"
                        :disabled="isSending || isDisabled || !canResendCode"
                        @click="resendIpVerification()"
                    >
                        {{ resendLinkLabel }}
                    </button>
                </p>
                <p v-if="hasPhone" class="ip-verification__footer-line">
                    <button
                        type="button"
                        class="ip-verification__text-action"
                        :disabled="isSending || isDisabled"
                        @click="openChannelModal()"
                    >
                        {{ $t('forms.auth.login.microcopy.try_another_way') }}
                    </button>
                </p>
            </div>
        </div>

        <common-modal v-if="isChannelModalOpen" width="medium" :closeOnClickAway="!isSending" @close="closeChannelModal()">
            <div class="ip-verification__channel-modal">
                <h3 class="ip-verification__channel-modal-title">
                    {{ $t('forms.auth.login.microcopy.choose_delivery') }}
                </h3>
                <div class="ip-verification__channel-picker">
                    <button
                        type="button"
                        class="ip-verification__channel-option"
                        :class="{ 'ip-verification__channel-option--active': verificationChannel === 'email' }"
                        :disabled="isSending || isDisabled"
                        @click="selectVerificationChannel('email')"
                    >
                        <common-icon name="email" size="small"></common-icon>
                        <span class="ip-verification__channel-option-copy">
                            <span class="ip-verification__channel-option-label">
                                {{ $t('forms.auth.login.microcopy.send_by_email') }}
                            </span>
                            <span v-if="maskedEmail" class="ip-verification__channel-option-value">
                                {{ maskedEmail }}
                            </span>
                        </span>
                    </button>
                    <button
                        type="button"
                        class="ip-verification__channel-option"
                        :class="{ 'ip-verification__channel-option--active': verificationChannel === 'sms' }"
                        :disabled="isSending || isDisabled"
                        @click="selectVerificationChannel('sms')"
                    >
                        <common-icon name="phone" size="small"></common-icon>
                        <span class="ip-verification__channel-option-copy">
                            <span class="ip-verification__channel-option-label">
                                {{ $t('forms.auth.login.microcopy.send_by_sms') }}
                            </span>
                            <span v-if="maskedPhone" class="ip-verification__channel-option-value">
                                {{ maskedPhone }}
                            </span>
                        </span>
                    </button>
                </div>
                <common-buttons align="center">
                    <common-button
                        :label="$t('forms.auth.login.microcopy.cancel')"
                        type="secondary"
                        :isSubmittable="false"
                        :isDisabled="isSending || isDisabled"
                        @click="closeChannelModal()"
                    ></common-button>
                </common-buttons>
            </div>
        </common-modal>

        <template v-if="!requiresIpVerification">
            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.auth.login.fields.email.label')"
                        :placeholder="$t('forms.auth.login.fields.email.placeholder')"
                        :hasAutofocus="true"
                        :isRequired="true"
                        :formData="data"
                        :error="errorMessage"
                        :isDisabled="isSending || isDisabled"
                        valuePath="email"
                        type="email"
                        @change="changeEmail($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <common-buttons align="center" class="resend-verification">
                <common-button
                    v-if="notVerifed"
                    :label="$t('forms.auth.login.microcopy.resend')"
                    type="primary"
                    :isSubmittable="false"
                    @click="resendVerification()"
                ></common-button>
            </common-buttons>

            <form-row v-if="!notVerifed">
                <form-cell>
                    <text-field
                        :label="$t('forms.auth.login.fields.password.label')"
                        :placeholder="$t('forms.auth.login.fields.password.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :isDisabled="isSending || isDisabled"
                        valuePath="password"
                        :type="passwordInputFieldType"
                        :maxLength="40"
                        @change="change($event)"
                    >
                        <template #right>
                            <router-link :to="{ name: 'auth.forgot-password' }" tabindex="1">
                                {{ $t('forms.auth.login.microcopy.forgot_password') }}
                            </router-link>
                        </template>
                        <template #icon>
                            <common-toggle-visibility-button
                                @visibility-toggle-switch="showPassword"
                            ></common-toggle-visibility-button>
                        </template>
                    </text-field>
                </form-cell>
            </form-row>
        </template>

        <template v-if="!requiresIpVerification && !notVerifed" #footer>
            <common-buttons>
                <common-button
                    :isProcessing="isSending || isDisabled"
                    :label="$t('forms.auth.login.microcopy.login')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>

        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </form-wrapper>
</template>

<script>
    import dayjs from 'dayjs'
    import FormMixin from 'mixins/FormMixin'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'
    import AuthAPI from 'api/AuthAPI'
    import LogsTrackerAPI from 'api/LogsTrackerAPI'

    export default {
        mixins: [FormMixin, PasswordToggleVisibilityMixin],

        props: {
            isDisabled: { type: Boolean, default: false },
        },

        data() {
            return {
                errorMessage: null,
                notVerifed: false,
                notificationMessage: '',
                requiresIpVerification: false,
                verifyTokenError: null,
                canResendAt: null,
                pendingAuthTokens: null,
                nowTick: Date.now(),
                resendCooldownTimer: null,
                codeInputKey: 0,
                verificationChannel: 'email',
                maskedDestination: null,
                maskedEmail: null,
                maskedPhone: null,
                hasPhone: false,
                isChannelModalOpen: false,
            }
        },

        computed: {
            isSmsChannel() {
                return this.verificationChannel === 'sms'
            },

            verificationMessage() {
                return this.isSmsChannel
                    ? this.$t('forms.auth.login.microcopy.ip_verification_sent_sms')
                    : this.$t('forms.auth.login.microcopy.ip_verification_sent')
            },

            destinationDisplay() {
                if (this.isSmsChannel) {
                    return this.maskedPhone || this.maskedDestination || ''
                }
                return this.maskedEmail || this.maskedDestination || this.data.email || ''
            },

            isVerifyCodeComplete() {
                return (this.data.verifyToken || '').trim().length === 6
            },

            canResendCode() {
                if (!this.canResendAt) return true
                return dayjs(this.nowTick).isAfter(dayjs(this.canResendAt))
            },

            resendLinkLabel() {
                if (this.canResendCode) {
                    return this.$t('forms.auth.login.microcopy.resend_short')
                }

                const secondsLeft = Math.max(0, dayjs(this.canResendAt).diff(dayjs(this.nowTick), 'second'))
                return this.$t('forms.auth.login.microcopy.resend_short_wait', { seconds: secondsLeft })
            },
        },

        methods: {
            async getClientIpAddress() {
                return fetch('https://api.ipify.org?format=json')
                    .then((res) => res.json())
                    .then(({ ip }) => ip)
                    .catch(() => null)
            },

            logLoginAttempt({ email, status, error, verifyToken, resend, channel }) {
                return this.getClientIpAddress().then((ipAddress) =>
                    LogsTrackerAPI.create({
                        email,
                        eventType: 'LOGIN',
                        status,
                        ipAddress,
                        userAgent: navigator.userAgent,
                        failureReason: error?.message,
                        verifyToken: verifyToken || undefined,
                        resend: resend || undefined,
                        channel: channel || undefined,
                    }),
                )
            },

            applyVerificationChannelMeta(response) {
                if (!response) return
                if (response.channel) this.verificationChannel = response.channel
                if (typeof response.hasPhone === 'boolean') this.hasPhone = response.hasPhone
                if (response.maskedEmail) this.maskedEmail = response.maskedEmail
                if (response.maskedPhone !== undefined) this.maskedPhone = response.maskedPhone
                if (response.maskedDestination) {
                    this.maskedDestination = response.maskedDestination
                } else if (response.channel === 'sms') {
                    this.maskedDestination = this.maskedPhone
                } else {
                    this.maskedDestination = this.maskedEmail
                }
            },

            completeLogin() {
                const { accessToken, idToken, refreshToken } = this.pendingAuthTokens
                this.$auth.logIn(accessToken, idToken, refreshToken)
                this.clearIpVerificationState()
                this.emitEvent({ name: 'sent' })
                this.isSending = false
            },

            resetVerificationCodeInput() {
                this.$set(this.data, 'verifyToken', '')
                this.codeInputKey += 1
            },

            enterIpVerification({ canResendAt, channel, hasPhone, maskedDestination, maskedEmail, maskedPhone }) {
                this.requiresIpVerification = true
                this.verifyTokenError = null
                this.resetVerificationCodeInput()
                this.canResendAt = canResendAt || null
                this.verificationChannel = channel || 'email'
                this.hasPhone = Boolean(hasPhone)
                this.maskedEmail = maskedEmail || null
                this.maskedPhone = maskedPhone || null
                this.maskedDestination =
                    maskedDestination || (channel === 'sms' ? this.maskedPhone : this.maskedEmail || this.data.email)
                this.isChannelModalOpen = false
                this.startResendCooldownTimer()
                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                this.isSending = false
            },

            clearIpVerificationState() {
                this.requiresIpVerification = false
                this.verifyTokenError = null
                this.canResendAt = null
                this.pendingAuthTokens = null
                this.verificationChannel = 'email'
                this.maskedDestination = null
                this.maskedEmail = null
                this.maskedPhone = null
                this.hasPhone = false
                this.isChannelModalOpen = false
                this.resetVerificationCodeInput()
                this.stopResendCooldownTimer()
            },

            startResendCooldownTimer() {
                this.stopResendCooldownTimer()
                this.nowTick = Date.now()
                this.resendCooldownTimer = setInterval(() => {
                    this.nowTick = Date.now()
                    if (this.canResendCode) {
                        this.stopResendCooldownTimer()
                    }
                }, 1000)
            },

            stopResendCooldownTimer() {
                if (this.resendCooldownTimer) {
                    clearInterval(this.resendCooldownTimer)
                    this.resendCooldownTimer = null
                }
            },

            handleIpVerificationResponse(response) {
                if (response?.requireValidation) {
                    this.enterIpVerification({
                        canResendAt: response.canResendAt,
                        channel: response.channel,
                        hasPhone: response.hasPhone,
                        maskedDestination: response.maskedDestination,
                        maskedEmail: response.maskedEmail,
                        maskedPhone: response.maskedPhone,
                    })
                    return true
                }

                this.completeLogin()
                return false
            },

            getVerifyTokenFieldError(error) {
                const fieldError = error?.response?.data?.[0]
                if (fieldError?.field !== 'verifyToken') return null

                if (fieldError.code === 'entity.expired') {
                    return this.$t('forms.auth.login.microcopy.ip_verification_expired')
                }
                if (fieldError.code === 'entity.invalid') {
                    return this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                }
                return fieldError.message
            },

            handleFormSubmission({ email, password, verifyToken }) {
                if (this.requiresIpVerification) {
                    return this.submitIpVerification({ email, verifyToken })
                }

                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: true } })
                this.handleRequest(AuthAPI.logIn(email, password))
                    .then((AuthenticationResult) => {
                        const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                        this.pendingAuthTokens = {
                            accessToken: accessToken.jwtToken,
                            idToken: idToken.jwtToken,
                            refreshToken: refreshToken.token,
                        }

                        return this.logLoginAttempt({ email, status: 'SUCCESS', channel: 'email' }).then((response) => {
                            this.handleIpVerificationResponse(response)
                        })
                    })
                    .catch((error) => {
                        this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })

                        if (this.pendingAuthTokens) {
                            this.pendingAuthTokens = null
                            this.errorMessage = this.$t('forms.auth.login.microcopy.ip_verification_resend_failed')
                            this.data.password = ''
                            AuthAPI.logOut().catch(() => {})
                            return
                        }

                        if (error.message?.includes('PasswordExpired')) {
                            this.$router.push({ name: 'auth.forgot-password', query: { email, isPasswordExpired: true } })
                        }
                        if (error.code === 'UserNotConfirmedException') {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_verified')
                            this.notVerifed = true
                        } else {
                            this.errorMessage = this.$t('forms.auth.login.microcopy.not_found_error')
                        }
                        this.data.password = ''
                        return this.logLoginAttempt({ email, status: 'FAILURE', error })
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            submitIpVerification({ email, verifyToken }) {
                const code = (verifyToken || '').trim().toUpperCase()
                this.verifyTokenError = null

                if (code.length !== 6) {
                    this.verifyTokenError = this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                    return
                }

                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: true } })
                this.handleRequest(this.logLoginAttempt({ email, status: 'SUCCESS', verifyToken: code }))
                    .then((response) => {
                        this.handleIpVerificationResponse(response)
                    })
                    .catch((error) => {
                        this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                        this.verifyTokenError =
                            this.getVerifyTokenFieldError(error) || this.$t('forms.auth.login.microcopy.ip_verification_invalid')
                        this.resetVerificationCodeInput()
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            resendIpVerification() {
                if (!this.canResendCode) return

                this.verifyTokenError = null
                this.handleRequest(
                    this.logLoginAttempt({
                        email: this.data.email,
                        status: 'SUCCESS',
                        resend: true,
                        channel: this.verificationChannel,
                    }),
                )
                    .then((response) => {
                        if (response?.requireValidation) {
                            this.applyVerificationChannelMeta(response)
                            this.canResendAt = response.canResendAt || null
                            this.startResendCooldownTimer()
                            this.notificationMessage = this.isSmsChannel
                                ? this.$t('forms.auth.login.microcopy.ip_verification_resent_sms')
                                : this.$t('forms.auth.login.microcopy.ip_verification_resent')
                            this.$refs.commonSuccessNotificationChild.showNotification()
                        }
                    })
                    .catch((error) => {
                        const fieldError = error?.response?.data?.[0]
                        this.verifyTokenError =
                            fieldError?.message || this.$t('forms.auth.login.microcopy.ip_verification_resend_failed')
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            openChannelModal() {
                this.isChannelModalOpen = true
                this.verifyTokenError = null
            },

            closeChannelModal() {
                this.isChannelModalOpen = false
                this.verifyTokenError = null
            },

            selectVerificationChannel(channel) {
                if (channel === 'sms' && !this.hasPhone) {
                    this.verifyTokenError = this.$t('forms.auth.login.microcopy.ip_verification_no_phone')
                    return
                }

                if (channel === this.verificationChannel) {
                    this.closeChannelModal()
                    return
                }

                this.closeChannelModal()
                this.verifyTokenError = null
                this.handleRequest(
                    this.logLoginAttempt({
                        email: this.data.email,
                        status: 'SUCCESS',
                        resend: true,
                        channel,
                    }),
                )
                    .then((response) => {
                        if (!response?.requireValidation) return

                        this.applyVerificationChannelMeta(response)
                        this.canResendAt = response.canResendAt || null
                        this.startResendCooldownTimer()

                        if (response.codeSent === false) {
                            const secondsLeft = Math.max(0, dayjs(this.canResendAt).diff(dayjs(), 'second'))
                            this.verifyTokenError = this.$t('forms.auth.login.microcopy.resend_code_wait', {
                                seconds: secondsLeft,
                            })
                            return
                        }

                        this.resetVerificationCodeInput()
                        this.notificationMessage =
                            channel === 'sms'
                                ? this.$t('forms.auth.login.microcopy.ip_verification_resent_sms')
                                : this.$t('forms.auth.login.microcopy.ip_verification_resent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .catch((error) => {
                        const fieldError = error?.response?.data?.[0]
                        if (fieldError?.field === 'channel') {
                            this.verifyTokenError =
                                fieldError.message || this.$t('forms.auth.login.microcopy.ip_verification_no_phone')
                        } else {
                            this.verifyTokenError =
                                fieldError?.message || this.$t('forms.auth.login.microcopy.ip_verification_resend_failed')
                        }
                    })
                    .finally(() => {
                        this.isSending = false
                        this.isChannelModalOpen = false
                    })
            },

            cancelIpVerification() {
                this.clearIpVerificationState()
                this.data.password = ''
                this.emitEvent({ name: 'form-submitted', payload: { areFieldsDisabled: false } })
                AuthAPI.logOut().catch(() => {})
            },

            changeEmail(event) {
                this.change(event)
                this.errorMessage = ''
            },

            onVerificationCodeChange(code) {
                this.$set(this.data, 'verifyToken', code)
                this.verifyTokenError = null
            },

            resendVerification() {
                this.handleRequest(AuthAPI.resendVerificationEmail(this.data.email))
                    .then(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                        this.notificationMessage = this.$t('forms.auth.login.microcopy.email_sent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .catch(() => {
                        this.notVerifed = false
                        this.errorMessage = ''
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },

        watch: {
            requiresIpVerification(isRequired) {
                this.emitEvent({ name: 'ip-verification-changed', payload: isRequired })
            },
        },

        mounted() {
            if (this.$route.query.email) {
                this.data.email = this.$route.query.email
            }
        },

        beforeDestroy() {
            this.stopResendCooldownTimer()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .resend-verification {
        margin-top: 20px;
    }

    .ip-verification {
        text-align: left;

        &__heading {
            margin: 0 0 rem(8px);
            color: $color--txt-header;
            font-size: rem(28px);
            font-weight: 700;
            line-height: $line-height--heading;
            letter-spacing: $letter-spacing--tight;
            text-align: left;
        }

        &__message {
            margin: 0 0 rem(20px);
            color: $color--txt-base-8;
            font-size: rem($font-size--form);
            line-height: $line-height--copy;
            text-align: left;
        }

        &__destination {
            display: flex;
            align-items: center;
            gap: rem(12px);
            margin-bottom: rem(20px);
            padding: rem(12px) rem(14px);
            background: $color-white;
            border: 1px solid $color--border-base;
            border-radius: rem(12px);
        }

        &__destination-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: rem(40px);
            height: rem(40px);
            color: $color--accent-1;
            background: rgba($color--accent-1, 0.12);
            border-radius: rem(10px);
        }

        &__destination-copy {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
        }

        &__destination-label {
            color: $color--txt-minor;
            font-size: rem($font-size--hint);
            line-height: $line-height--hint;
        }

        &__destination-value {
            overflow: hidden;
            color: $color--txt-header;
            font-size: rem($font-size--form);
            font-weight: 700;
            line-height: 1.4;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__channel-modal {
            padding: rem(8px) 0 rem(4px);
        }

        &__channel-modal-title {
            margin: 0 0 rem(16px);
            color: $color--txt-header;
            font-size: rem(20px);
            font-weight: 700;
            line-height: $line-height--heading;
            text-align: center;
        }

        &__channel-picker {
            display: flex;
            flex-direction: column;
            gap: rem(8px);
            margin-bottom: rem(24px);
        }

        &__channel-option {
            display: flex;
            align-items: center;
            gap: rem(10px);
            width: 100%;
            padding: rem(10px) rem(12px);
            color: $color--txt-header;
            background: transparent;
            border: 1px solid $color--border-base;
            border-radius: rem(10px);
            font-family: inherit;
            font-size: rem($font-size--form);
            font-weight: 600;
            text-align: left;
            cursor: pointer;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                border-color: $color--accent-1;
            }

            &--active {
                color: $color--accent-1;
                border-color: $color--accent-1;
                background: rgba($color--accent-1, 0.08);
            }

            &:disabled {
                opacity: $opacity--is-disabled;
                cursor: not-allowed;
            }
        }

        &__channel-option-copy {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
            gap: rem(2px);
        }

        &__channel-option-label {
            font-weight: 600;
            line-height: 1.3;
        }

        &__channel-option-value {
            overflow: hidden;
            color: $color--txt-minor;
            font-size: rem($font-size--hint);
            font-weight: 500;
            line-height: $line-height--hint;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__text-action {
            padding: 0;
            color: $color--accent-1;
            background: none;
            border: 0;
            font-family: inherit;
            font-size: inherit;
            font-weight: 700;
            letter-spacing: $letter-spacing--loose;
            text-transform: uppercase;
            cursor: pointer;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                color: $color--accent-3;
            }

            &:disabled {
                opacity: $opacity--is-disabled;
                cursor: not-allowed;
            }
        }

        &__error {
            margin: rem(12px) 0 0;
            color: $color--accent-4;
            font-size: rem($font-size--form);
            line-height: 1.4;
            text-align: center;
        }

        &__actions {
            display: flex;
            gap: rem(12px);
            margin-top: rem(28px);

            > * {
                flex: 1;
                min-width: 0;
            }
        }

        &__verify--ready {
            box-shadow: 0 rem(8px) rem(20px) rgba($color--accent-1, 0.35);
        }

        &__footer {
            margin-top: rem(16px);
            text-align: center;
        }

        &__footer-line {
            margin: 0;
            color: $color--txt-base;
            font-size: rem($font-size--form);
            line-height: 1.5;

            & + & {
                margin-top: rem(8px);
            }
        }

        @include breakpoint(smartphone) {
            &__heading {
                font-size: rem(24px);
            }

            &__actions {
                flex-direction: column-reverse;
            }
        }
    }
</style>
